import { type HeadFC, type PageProps, graphql } from 'gatsby';

import { CallToAction } from '@components/CallToAction';
import { Container } from '@components/Container';
import { Feature } from '@components/Feature';
import { FeatureList } from '@components/Feature/FeatureList';
import { Hero } from '@components/Hero';
import { NewsList } from '@components/NewsList';
import { NumberedList } from '@components/NumberedList';
import { Stats } from '@components/Stats';
import { Testimonial } from '@components/Testimonials/Testimonial';
import { TestimonialsCloud } from '@components/Testimonials/TestimonialsCloud';

import ArrowFeature from '@assets/images/arrow-feature.svg';
import ArrowHomeServices from '@assets/images/arrow-home-services.svg';
import ArrowHomeTestimonial from '@assets/images/arrow-home-testimonial.svg';
import ArrowSectors from '@assets/images/arrow-sectors.svg';

const HomePage: React.FC<PageProps<Queries.HomePageQuery>> = ({ data }) => {
  const { page, services, sectors } = data;

  return (
    <div id="home">
      <Container>
        <div className="py-8">
          <Hero variant="horizontal" data={page?.hero} />
        </div>

        {/* <div className="py-8 lg:py-16">
          <LogosList title={page?.brands_title} data={page.logos} />
        </div> */}
      </Container>

      <div className="relative -z-10">
        <ArrowHomeTestimonial className="absolute left-0 top-48 lg:top-0" />
      </div>

      <Container>
        <div className="py-16 lg:py-32">
          <Testimonial
            data={page?.testimonial}
            imgClassName="lg:w-[600px]"
            contentClassName="text-xl"
          />
        </div>
      </Container>

      <div className="relative -z-10">
        <ArrowFeature className="absolute -top-8 right-0 lg:top-24" />
      </div>

      <Container>
        <div className="pb-8 pt-16 lg:py-20">
          <Feature data={page?.feature_1} />
        </div>

        <div className="py-8 lg:py-16">
          <Stats data={page?.stats} />
        </div>
      </Container>

      <div className="relative -z-10">
        <ArrowHomeServices className="absolute right-0 top-[34rem]" />
      </div>

      <Container>
        <div className="py-16" id="services">
          <FeatureList
            data={{
              title: page?.services_title,
              items: services?.nodes,
              linkLabel: page?.services_know_more,
            }}
            variant="primary"
            elevated
          />
        </div>

        <div className="py-8 lg:py-16">
          <CallToAction variant="cards" data={page?.cta_1} />
        </div>
      </Container>

      <div className="relative -z-10">
        <ArrowSectors className="absolute right-0 top-40" />
      </div>

      <Container>
        <div className="py-20 lg:py-32">
          <NumberedList
            variant="vertical"
            itemMarker="arrow"
            title={page?.sectors_title}
            data={sectors?.nodes}
          />
        </div>
      </Container>

      <div className="bg-neutral-50 py-16">
        <NewsList
          data={page?.news}
          items={page?.news?.items?.map((item) => ({
            ...item,
            author: { name: item.author_name, picture: item.author_image },
          }))}
        />
      </div>

      <Container>
        {page?.testimonials?.length > 0 && (
          <div className="py-8 lg:py-[6.5rem]">
            <TestimonialsCloud
              title={page?.testimonials_title}
              data={page?.testimonials.slice(0, 5)}
            />
          </div>
        )}

        <div className="py-16 lg:py-20">
          <CallToAction variant="single" data={[page?.cta_2]} />
        </div>
      </Container>
    </div>
  );
};

export default HomePage;

export const Head: HeadFC = ({ data, pageContext }) => (
  <title>
    {`${data?.site?.siteMetadata?.title} | ${pageContext?.navigation?.home?.label}`}
  </title>
);

export const query = graphql`
  query HomePage($locale: String) {
    site {
      siteMetadata {
        title
      }
    }
    page: strapiHome(locale: { eq: $locale }) {
      hero {
        title {
          intro
          main {
            data {
              childMarkdownRemark {
                html
              }
            }
          }
        }
        content {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
        image {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        link {
          label
          href
        }
      }
      brands_title
      # logos {
      #   localFile {
      #     publicURL
      #     childImageSharp {
      #       gatsbyImageData(placeholder: NONE)
      #     }
      #   }
      # }
      testimonial {
        title
        content
        author_name
        author_title
        right_image {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        image {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData(width: 120, height: 120)
            }
          }
        }
      }
      feature_1 {
        title {
          intro
          main {
            data {
              childMarkdownRemark {
                html
              }
            }
          }
        }
        content {
          data {
            childMarkdownRemark {
              html
              rawMarkdownBody
            }
          }
        }
        image {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      stats {
        title
        description
        icon {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      services_title {
        intro
        main {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
      }
      services_know_more
      cta_1 {
        title {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
        content {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
        link {
          label
          href
        }
        image {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      sectors_title {
        intro
        main {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
      }
      news {
        title {
          intro
          main {
            data {
              childMarkdownRemark {
                html
              }
            }
          }
        }
        link {
          label
          href
        }
        items {
          title
          short_description: description
          meta
          cover_image: image {
            localFile {
              publicURL
              childImageSharp {
                gatsbyImageData
              }
            }
          }
          author_name
          author_image {
            localFile {
              publicURL
              childImageSharp {
                gatsbyImageData
              }
            }
          }
        }
      }
      testimonials_title {
        intro
        main {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
      }
      testimonials {
        title
        content
        author_name
        author_title
        image {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
      cta_2 {
        title {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
        content {
          data {
            childMarkdownRemark {
              html
            }
          }
        }
        link {
          label
          href
        }
      }
    }
    brands: allStrapiBrand(limit: 5) {
      nodes {
        title
        logo {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }
    services: allStrapiService(
      sort: { createdAt: ASC }
      filter: { locale: { eq: $locale } }
    ) {
      nodes {
        title
        content: description
        image: icon {
          localFile {
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
        link {
          href
        }
      }
    }
    sectors: allStrapiSector(
      sort: { createdAt: ASC }
      filter: { locale: { eq: $locale } }
    ) {
      nodes {
        title
        link {
          href
        }
      }
    }
  }
`;
